import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useDebounce } from "../../../../hooks/useDebounce";
import { wishList } from "../../../../services/wishlistService";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { lngDetect } from "../../../../helpers/lngDetect";
import EventItemLoader from "../../../loader/EventItemLoader";
import EventItem from "../../../items/EventItem";
import Button from "../../../form/Button";
import OrganizationItem from "../../../items/OrganizationItem";
import NoData from "../../../elements/Nodata";
import noDataImage from "../../../../assets/images/organization.svg";
import { classNames } from "../../../../helpers/classNames";

const OrganizationList = ({ listClass }) => {
  const { lang } = useSelector((state) => state?.langProvider);
  const [removedItems, setRemovedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const { data: usrData } = useSelector((state) => state?.profile);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 8,
    page: 1,
    sortQuery: "ordering",
  });

  const {
    data: wishlistData,
    isLoading: wishlistLoading,
    isFetching: wishlistFetching,
    refetch,
  } = useQuery({
    queryKey: ["wishlist-organization", filters, search],
    queryFn: async (data) => {
      const res = await wishList({
        ...filters,
        ...(search && { keyword: search }),
        type: "wishlist-organization",
        user: usrData?._id,
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      if (res?.docs?.status === 200) {
        const new_data_arr = res?.docs?.data?.map((item) => {
          return {
            ...item,
            _id: item?.organizationData?._id,
            link: `/organization/details/${item?.organizationData?.uid}`,
            images: [item?.organizationData?.logo?.url],
            organizationTitle: lngDetect(item?.organizationData?.name),
            rating: "",
            latestReview: "",
            tags: "",
            organizationMinRate: "",
          };
        });
        if (search) {
          return {
            ...res?.docs,
            data: [...new_data_arr],
          };
        } else {
          if (filters?.page > 1) {
            return {
              ...res?.docs,
              data: [...(wishlistData?.data || []), ...new_data_arr]?.filter(
                (item, index, self) =>
                  index === self.findIndex((t) => t?._id === item?._id)
              ),
            };
          } else {
            return {
              ...res?.docs,
              data: new_data_arr,
            };
          }
        }
      } else {
        return {
          ...res,
          data: [],
        };
      }
    },
    placeholderData: keepPreviousData,
  });

  async function addToFavoriteResponse(res) {
    if (res?.status === 200) {
      wishList({
        ...filters,
        ...(search && { keyword: search }),
        type: "wishlist-organization",
        user: usrData?._id,
      }).then((resp) => {
        queryClient.setQueryData(
          ["wishlist-organization", filters, search],
          (state) => {
            const prevData = state?.data || [];
            const res_data = resp?.docs?.data?.map((item) => {
              return {
                ...item,
                _id: item?.organizationData?._id,
                link: `/organization/details/${item?.organizationData?.uid}`,
                images: [item?.organizationData?.logo?.url],
                organizationTitle: lngDetect(item?.organizationData?.name),
                rating: "",
                latestReview: "",
                tags: "",
                organizationMinRate: "",
              };
            });

            // Remove the target item directly
            const filteredData = prevData.filter(
              (item) => item?._id !== res?.data?._id
            );
            // Build a Set of existing IDs
            const existingIds = new Set(filteredData.map((item) => item?._id));
            // Only take new items that aren't already in state
            const result = (res_data || []).filter(
              (item) => !existingIds.has(item?._id)
            );

            return {
              ...state,
              data: [...filteredData, ...result],
              metadata: resp?.docs?.metadata,
            };
          }
        );
      });
    }
  }

  const loadMore = () => {
    if (wishlistData?.metadata?.hasNextPage) {
      setFilters((pre) => ({
        ...pre,
        page: wishlistData?.metadata?.nextPage,
      }));
    }
  };

  function getFollowManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      queryClient.setQueryData(
        ["wishlist-organization", filters, search],
        (state) => {
          let state_data = JSON.parse(JSON.stringify(state || {}));
          state_data.data = state_data?.data?.map((item) =>
            item?._id === res?.data?.to
              ? { ...item, isFollowing: res?.data?.status !== "cancelled" }
              : item
          );
          return { ...state_data };
        }
      );
    }
  }

  return (
    <>
      {wishlistLoading ? (
        <div
          className={classNames(
            "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 2xl:gap-8",
            listClass
          )}
        >
          {Array.from({ length: 8 }, (_, i) => (
            <EventItemLoader key={i} />
          ))}
        </div>
      ) : wishlistData?.data?.length > 0 ? (
        <div
          className={classNames(
            "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 2xl:gap-8",
            listClass
          )}
        >
          {wishlistData?.data.map((organization) => (
            <OrganizationItem
              key={organization?.id || organization?._id}
              data={organization}
              addToFavoriteResponse={addToFavoriteResponse}
              getResponse={getFollowManageResponse}
            />
          ))}
        </div>
      ) : (
        <NoData
          image={noDataImage}
          message="Currently you are not wishlisted any organization"
        />
      )}
      {
        wishlistData?.data?.length > 0 && (
          // wishlistData?.data?.length < wishlistData?.metadata?.totalDocs && (
          <div className="flex space-y-4 items-center justify-between mt-6">
            <div className="text-deep-purple text-xl 2xl:text-2xl font-semibold text-center">
              Showing {wishlistData?.data?.length} of{" "}
              {wishlistData?.metadata?.totalDocs}
            </div>
            {wishlistData?.metadata?.hasNextPage && (
              <div className="flex justify-center">
                <Button
                  buttonLabel="Load More"
                  buttonLabelClasses="text-lg uppercase text-white"
                  buttonClasses="h-14 bg-secondary-gradient"
                  buttonFunction={loadMore}
                  {...(wishlistFetching && {
                    loading: true,
                    buttonIconPosition: "left",
                    isDisable: true,
                  })}
                />
              </div>
            )}
          </div>
        )
        // )
      }
    </>
  );
};

export default OrganizationList;
